import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import VehicleSelector from './VehicleSelector';
import CustomerModal from './CustomerModal';
import VehicleDamageSketch from './VehicleDamageSketch';
import PreServiceChecklist from './PreServiceChecklist';

const JobCardForm = ({ onClose, onSuccess }) => {
  const { organization } = useOrganization();
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    customerId: '',
    vehicleId: '',
    vehicleNumber: '',
    vinNumber: '',
    odometerReading: '',
    branchId: '',
    appointmentDate: new Date().toISOString().split('T')[0],
    appointmentTime: new Date().toTimeString().slice(0, 5),
    estimatedTime: '',
    comments: '',
    referral: '',
    warrantyDate: '',
    discount: 0,
    servicesMargin: 10,
    partsMargin: 15,
    technicianId: ''
  });

  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [availableParts, setAvailableParts] = useState([]);
  const [parts, setParts] = useState([]);
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(5);
  const [damagePoints, setDamagePoints] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, branchesRes, servicesRes, partsRes, settingsRes, techniciansRes] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/branches'),
        axios.get('/api/services'),
        axios.get('/api/parts'),
        axios.get('/api/settings/config'),
        axios.get('/api/members?role=TECHNICIAN')
      ]);
      
      setCustomers(customersRes.data.customers || customersRes.data || []);
      setBranches(branchesRes.data.branches || branchesRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
      setAvailableParts(partsRes.data.parts || partsRes.data || []);
      setTechnicians(techniciansRes.data.members || techniciansRes.data || []);
      
      // Set VAT percentage from settings
      const settings = settingsRes.data;
      if (settings && settings.taxRate !== null && settings.taxRate !== undefined) {
        setVatPercentage(parseFloat(settings.taxRate));
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        vehicleNumber: formData.vehicleNumber || '',
        vinNumber: formData.vinNumber || '',
        odometerReading: formData.odometerReading || '',
        services: selectedServices,
        parts,
        damagePoints,
        checklist,
        estimatedTime: parseInt(formData.estimatedTime) || null,
        discount: parseFloat(formData.discount) || 0,
        servicesMargin: parseFloat(formData.servicesMargin) || 0,
        partsMargin: parseFloat(formData.partsMargin) || 0,
        selectedVehicle: selectedVehicle,
        technicianId: formData.technicianId ? parseInt(formData.technicianId) : null
      };
      
      if (formData.appointmentDate && formData.appointmentTime) {
        submitData.appointmentDate = `${formData.appointmentDate}T${formData.appointmentTime}:00`;
      }
      
      if (!formData.warrantyDate) {
        delete submitData.warrantyDate;
      }

      const response = await axios.post('/api/job-cards', submitData);
      const jobCardId = response.data.data.id;
      
      // Upload photos if any
      if (photos.length > 0) {
        setUploadingPhoto(true);
        try {
          const formData = new FormData();
          photos.forEach(photo => formData.append('photos', photo.file));
          
          await axios.post(`/api/jobcards/${jobCardId}/photos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          });
        } catch (photoError) {
          const message = photoError.response?.data?.message || 'Some photos failed to upload';
          toast.error(`Job card created but ${message}`);
        } finally {
          setUploadingPhoto(false);
        }
      }
      
      toast.success('Job card created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create job card');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addService = () => {
    setSelectedServices([...selectedServices, { serviceId: '', quantity: 1, price: 0 }]);
  };

  const updateService = (index, field, value) => {
    const updated = [...selectedServices];
    updated[index][field] = value;
    
    if (field === 'serviceId') {
      const service = services.find(s => s.id === parseInt(value));
      if (service) {
        updated[index].price = parseFloat(service.price) || 0;
        updated[index].name = service.name;
      }
    }
    
    setSelectedServices(updated);
  };

  const removeService = (index) => {
    setSelectedServices(selectedServices.filter((_, i) => i !== index));
  };

  const addPart = () => {
    setParts([...parts, { partId: '', quantity: 1, price: 0 }]);
  };

  const updatePart = (index, field, value) => {
    const updated = [...parts];
    updated[index][field] = value;
    
    if (field === 'partId') {
      const part = availableParts.find(p => p.id === parseInt(value));
      if (part) {
        updated[index].price = part.price;
        updated[index].name = part.name;
      }
    }
    
    setParts(updated);
  };

  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const servicesTotal = selectedServices.reduce((sum, s) => sum + ((s.price || 0) * (s.quantity || 0)), 0);
    const partsTotal = parts.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 0)), 0);
    const subtotal = servicesTotal + partsTotal;
    const discount = parseFloat(formData.discount) || 0;
    const vat = (subtotal - discount) * (vatPercentage / 100);
    const total = subtotal - discount + vat;
    
    return { servicesTotal, partsTotal, subtotal, discount, vat, total };
  };

  const totals = calculateTotals();

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      e.target.value = '';
      return;
    }
    
    const validFiles = [];
    for (const file of files) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 2MB limit`);
        continue;
      }
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error(`${file.name} must be JPG or PNG`);
        continue;
      }
      validFiles.push(file);
    }
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, {
          file,
          preview: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };
  
  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Create Job Card</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Tabs */}
          <div className="flex border-b px-4 sm:px-6 pt-4 sm:pt-6 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 whitespace-nowrap ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Job Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('sketch')}
              className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 whitespace-nowrap ${activeTab === 'sketch' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Sketch
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('photos')}
              className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 whitespace-nowrap ${activeTab === 'photos' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Photos ({photos.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('checklist')}
              className={`px-3 sm:px-4 py-2 font-medium text-sm border-b-2 whitespace-nowrap ${activeTab === 'checklist' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Checklist
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {activeTab === 'details' && (
              <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch *</label>
              <select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Branch</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
              <div className="flex space-x-2">
                <select
                  name="customerId"
                  value={formData.customerId}
                  onChange={handleChange}
                  required
                  className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCustomerModal(true)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Technician</label>
              <select
                name="technicianId"
                value={formData.technicianId}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Technician</option>
                {technicians.map(technician => (
                  <option key={technician.id} value={technician.id}>
                    {technician.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Vehicle Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Vehicle *</label>
              {selectedVehicle && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedVehicle(null);
                    setFormData(prev => ({ ...prev, vehicleId: '', vehicleNumber: '', vinNumber: '' }));
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Clear
                </button>
              )}
            </div>

            {selectedVehicle ? (
              <>
                <div className="bg-white border border-blue-200 rounded-md p-3 mb-3">
                  <div className="font-medium text-sm text-blue-800">
                    {selectedVehicle.brand} {selectedVehicle.model}
                    {selectedVehicle.year ? ` (${selectedVehicle.year})` : ''}
                    {selectedVehicle.color ? ` — ${selectedVehicle.color}` : ''}
                    {selectedVehicle.isNew && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">New</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Plate Number</label>
                    <input
                      type="text"
                      name="vehicleNumber"
                      value={formData.vehicleNumber}
                      onChange={handleChange}
                      placeholder="e.g., ABC-1234"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">VIN#</label>
                    <input
                      type="text"
                      name="vinNumber"
                      value={formData.vinNumber}
                      onChange={handleChange}
                      placeholder="Vehicle Identification Number"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">ODO Meter</label>
                    <input
                      type="text"
                      name="odometerReading"
                      value={formData.odometerReading}
                      onChange={handleChange}
                      placeholder="e.g., 50000 km"
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowVehicleSelector(true)}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-blue-400 hover:text-blue-600 text-sm"
              >
                + Select or Add Vehicle from Master
              </button>
            )}
          </div>

          {/* Appointment */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Appointment Date</label>
              <input
                type="date"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input
                type="time"
                name="appointmentTime"
                value={formData.appointmentTime}
                onChange={handleChange}
                min={formData.appointmentDate === new Date().toISOString().split('T')[0] ? 
                  new Date().toTimeString().slice(0, 5) : undefined}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Time (min)</label>
              <input
                type="number"
                name="estimatedTime"
                value={formData.estimatedTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Services */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Services</label>
              <button
                type="button"
                onClick={addService}
                className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Service
              </button>
            </div>
            {selectedServices.map((service, index) => (
              <div key={index} className="grid grid-cols-4 sm:grid-cols-12 gap-2 mb-2">
                <div className="col-span-4 sm:col-span-5">
                  <select
                    value={service.serviceId}
                    onChange={(e) => updateService(index, 'serviceId', e.target.value)}
                    className="w-full px-2 py-2 border rounded text-sm"
                  >
                    <option value="">Select Service</option>
                    {services.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={service.quantity || ''}
                    onChange={(e) => updateService(index, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-2 py-2 border rounded text-sm"
                  />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <input
                    type="number"
                    placeholder="Price"
                    value={service.price || ''}
                    onChange={(e) => updateService(index, 'price', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="w-full px-2 py-2 border rounded text-sm"
                  />
                </div>
                <div className="col-span-1 sm:col-span-1 flex items-center">
                  <button
                    type="button"
                    onClick={() => removeService(index)}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Parts */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Parts</label>
              <button
                type="button"
                onClick={addPart}
                className="flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Part
              </button>
            </div>
            {parts.map((part, index) => (
              <div key={index} className="grid grid-cols-4 sm:grid-cols-12 gap-2 mb-2">
                <div className="col-span-4 sm:col-span-5">
                  <select
                    value={part.partId || ''}
                    onChange={(e) => updatePart(index, 'partId', e.target.value)}
                    className="w-full px-2 py-2 border rounded text-sm"
                  >
                    <option value="">Select Part</option>
                    {availableParts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} (Stock: {p.quantity})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={part.quantity || ''}
                    onChange={(e) => updatePart(index, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-2 py-2 border rounded text-sm"
                  />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <input
                    type="number"
                    placeholder="Price"
                    value={part.price || ''}
                    onChange={(e) => updatePart(index, 'price', parseFloat(e.target.value) || 0)}
                    step="0.01"
                    className="w-full px-2 py-2 border rounded text-sm"
                  />
                </div>
                <div className="col-span-1 sm:col-span-1 flex items-center">
                  <button
                    type="button"
                    onClick={() => removePart(index)}
                    className="text-red-600 hover:text-red-900 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">History</label>
              <textarea
                name="comments"
                value={formData.comments}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Referral</label>
                <input
                  type="text"
                  name="referral"
                  value={formData.referral}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Warranty Date</label>
                <input
                  type="date"
                  name="warrantyDate"
                  value={formData.warrantyDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>



                {/* Totals */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Services Total:</span>
                        <span>{formatCurrency(totals.servicesTotal, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Parts Total:</span>
                        <span>{formatCurrency(totals.partsTotal, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(totals.subtotal, organization?.currency_code)}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span>Discount:</span>
                        <input
                          type="number"
                          name="discount"
                          value={formData.discount}
                          onChange={handleChange}
                          step="0.01"
                          className="w-20 px-2 py-1 border rounded text-right"
                        />
                      </div>
                      <div className="flex justify-between">
                        <span>VAT ({vatPercentage}%):</span>
                        <span>{formatCurrency(totals.vat, organization?.currency_code)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>{formatCurrency(totals.total, organization?.currency_code)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'sketch' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Damage Assessment</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click on the vehicle diagram to mark damage points. You can add multiple points and describe each damage.
                </p>
                <div className="flex gap-6">
                  <div>
                    <VehicleDamageSketch
                      damagePoints={damagePoints}
                      onDamagePointsChange={setDamagePoints}
                    />
                  </div>
                  <div className="flex-1">
                    {damagePoints.length > 0 ? (
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">Damage Points Summary</h4>
                        <div className="space-y-2">
                          {damagePoints.map((point, index) => (
                            <div key={index} className="p-3 bg-gray-50 rounded">
                              <div>
                                <span className="font-medium text-sm">Point {index + 1}:</span>
                                <span className="ml-2 text-sm">{point.label}</span>
                              </div>
                              <span className="inline-block mt-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {point.damageType}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        No damage points marked yet. Click on the vehicle diagram to add damage points.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'photos' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Job Card Photos</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload up to 10 photos (max 2MB each). Accepted formats: JPG, PNG, JPEG.
                </p>
                
                {/* Upload Section */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handlePhotoUpload}
                    disabled={photos.length >= 10 || uploadingPhoto}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`cursor-pointer flex flex-col items-center justify-center space-y-2 ${
                      photos.length >= 10 || uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <div className="text-gray-400">
                      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-900">
                        {uploadingPhoto ? 'Uploading...' : 'Click to upload photos'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {photos.length}/10 photos uploaded
                      </p>
                    </div>
                  </label>
                </div>

                {/* Photo Thumbnails */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.preview}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          {(photo.file.size / 1024 / 1024).toFixed(1)}MB
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'checklist' && (
              <PreServiceChecklist checklist={checklist} onChange={setChecklist} />
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end space-x-3 p-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Job Card'}
            </button>
          </div>
        </form>
      </div>

      {/* Modals */}
      {showVehicleSelector && (
        <VehicleSelector
          customerId={formData.customerId}
          onSelect={(vehicle) => {
            setSelectedVehicle(vehicle);
            setFormData(prev => ({
              ...prev,
              vehicleId: vehicle.id,
              vehicleNumber: vehicle.plateNumber || prev.vehicleNumber,
              vinNumber: vehicle.vin || prev.vinNumber
            }));
            setShowVehicleSelector(false);
          }}
          onClose={() => setShowVehicleSelector(false)}
        />
      )}

      {showCustomerModal && (
        <CustomerModal
          onSuccess={(customer) => {
            setCustomers(prev => [...prev, customer]);
            setFormData(prev => ({ ...prev, customerId: customer.id }));
            setShowCustomerModal(false);
          }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  );
};

export default JobCardForm;
